import { FC, ReactNode, useContext } from 'react';
import { Group, Text, Card, Flex, Skeleton, Loader } from '@mantine/core';
import Badge from 'components/Badge';
import { ICallHistoryTableData } from '.';

import classes from './index.module.scss';
import { AccessLevel } from 'interfaces/GetUserResult';
import AuthContext from 'context/AuthContext';

interface TableColumnProps {
  children: ReactNode;
  label: string;
}

interface TableGroupProps {
  record?: ICallHistoryTableData;
  isLoading?: boolean;
}

export const TableColumn: FC<TableColumnProps> = ({ label, children }) => {
  return (
    <Flex
      gap="sm"
      justify={{ base: 'space-between', sm: 'start' }}
      align="start"
      w={{ base: '100%', xs: 'auto' }}
    >
      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
        {label}
      </Text>
      {children}
    </Flex>
  );
};

export const TableGroup: FC<TableGroupProps> = ({
  record,
  isLoading = false,
}) => {
  const {
    data: { account },
  } = useContext(AuthContext);
  const isPremium = account?.access_level === AccessLevel.PRO;

  return (
    <Card
      withBorder
      padding="0.75rem"
      radius="md"
      className={classes.card}
      style={{ minHeight: isPremium ? 'auto' : '200px' }}
    >
      <Group justify="space-between" gap="xs" mb="0.8rem">
        {isLoading ? (
          <>
            <Skeleton h={13} w={130.5} />
            <Skeleton h={16} w={53} />
          </>
        ) : (
          <>
            <Text fw={500} size="0.8rem" className={classes.phoneNumber}>
              {record?.phoneNumber}
            </Text>
            <Badge color={record?.statusColor} variant="light" size="sm">
              {record?.status}
            </Badge>
          </>
        )}
      </Group>
      <Flex
        gap="sm"
        direction={{ base: 'column', xs: 'row' }}
        align="start"
        justify="space-between"
        flex={{ base: 1, xs: 'auto' }}
      >
        <TableColumn label="Start Time">
          {isLoading ? (
            <Skeleton h={17} w={120} />
          ) : (
            <Text size="xs" ta="right" style={{ whiteSpace: 'nowrap' }}>
              {record?.startTime}
            </Text>
          )}
        </TableColumn>

        <TableColumn label="Duration">
          {isLoading ? (
            <Skeleton h={17} w={35} />
          ) : (
            <>
              {record?.isIncomplete ? (
                <Flex align="baseline" gap="xs">
                  <Text c="dimmed" fz="xs">
                    Ongoing
                  </Text>
                  <Loader size="0.75rem" type="dots" />
                </Flex>
              ) : (
                <Text size="xs" ta="right" style={{ whiteSpace: 'nowrap' }}>
                  {record?.duration}
                </Text>
              )}
            </>
          )}
        </TableColumn>

        <TableColumn label="Sentiment">
          {isLoading ? (
            <Skeleton h={17} w={72} />
          ) : (
            <Flex gap={4}>
              <Text size="xs" fw={500}>
                {record?.sentimentPercentage}
              </Text>
              <Text size="xs" c="dimmed" ta="right">
                {record?.sentiment}
              </Text>
            </Flex>
          )}
        </TableColumn>
      </Flex>
    </Card>
  );
};
